import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import {
	Button, Chip, Collapse, Divider, Drawer,
	FormControlLabel, Radio, RadioGroup, Slider,
	TextField, Typography, Checkbox, Card, Grid, FormControl,
	FormGroup, FormLabel, Switch, CardHeader, CardContent,
	colors, IconButton, Tooltip, Hidden
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup, Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import _ from 'underscore';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useRouter from 'utils/useRouter';
import { SelectField } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';
import CreateIcon from '@material-ui/icons/Create';
import EmailIcon from '@material-ui/icons/Email';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import EditIcon from '@material-ui/icons/EditOutlined';
import CancelIcon from '@material-ui/icons/Cancel';
import Label from '../../../../../../components/Label';
import DataTable from '../../../../../../components/DataTable/DataTable.js';
import {
	SetFormState, ToggleCarriersColorsDialog, ToggleCarriersColorsPickerDialog
} from '../../../../actions/FailuresMapActions';

const schema = {
	siteId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	jvcoId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	date: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	status: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	drawer: {
		width: 420,
		maxWidth: '100%',
	},
	header: {
		padding: theme.spacing(2, 1),
		display: 'flex',
		justifyContent: 'space-between',
	},
	buttonIcon: {
		marginRight: theme.spacing(1),
	},
	content: {
		padding: theme.spacing(0, 3),
		flexGrow: 1,
	},
	contentSection: {
		padding: theme.spacing(2, 0),
	},
	contentSectionHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		cursor: 'pointer',
	},
	contentSectionContent: {},
	formGroup: {
		padding: theme.spacing(2, 0),
	},
	fieldGroup: {
		display: 'flex',
		alignItems: 'center',
	},
	field: {
		marginTop: 0,
		marginBottom: 0,
	},
	flexGrow: {
		flexGrow: 1,
	},
	addButton: {
		marginLeft: theme.spacing(1),
	},
	tags: {
		marginTop: theme.spacing(1),
	},
	minAmount: {
		marginRight: theme.spacing(3),
	},
	maxAmount: {
		marginLeft: theme.spacing(3),
	},
	radioGroup: {},
	actions: {
		padding: theme.spacing(3),
		'& > * + *': {
			marginTop: theme.spacing(2),
		},
	},
}));

const CarriersColorsDialogForm = (props) => {
	const { className, ...rest } = props;
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.failuresMapReducer.formState);

	const carriersColors = useSelector(state => state.failuresMapReducer.carriersColors);

	useEffect(() => {

	}, []);

	const cardStyle = {
		// height: '10vh'
	};

	const _editColor = (tableIdColorCode, currentColorCode) => {
		dispatch(ToggleCarriersColorsDialog(false));
		dispatch(ToggleCarriersColorsPickerDialog(true, tableIdColorCode, currentColorCode));
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title="Edit Color">
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						onClick={() => _editColor(rowData[0], rowData[2])}
					>
						<EditIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	const _renderActionsColor = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<div hidden={rowData[2] === null}>
					<Button
						style={{
							backgroundColor: rowData[2],
							borderColor: rowData[2],
							fontWeight: 'bold',
							width: '35px',
							height: '15px'
						}}
						onClick={() => _editColor(rowData[0], rowData[2])}
					/>
				</div>
			</>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'carrier',
				label: 'Carrier',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'colorCode',
				label: 'Color Code',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'color',
				label: 'Color',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActionsColor(tableMeta)
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: false,
		expandableRows: false, // Try Adding This
	});

	return (
		<Card style={cardStyle}>
			<div className={classes.content}>
				<div className={classes.contentSection}>
					<Grid container spacing={1}>
						<Grid item md={formState.values.mapCompare ? 6 : 12}>
							<DataTable
								id="carriersColorsDataTble"
								title=""
								data={carriersColors}
								columns={_getTableColumns()}
								rowsPerPage={10}
								tableBodyMaxHeight="30em"
								tableBodyCellMaxWidth="250"
								options={_getTableOptions()}
							/>
						</Grid>
					</Grid>
				</div>
			</div>
		</Card>
	);
};

CarriersColorsDialogForm.propTypes = {
	className: PropTypes.string,
};

export default CarriersColorsDialogForm;
